import { Skeleton } from '@design-system';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { PaymentMethod, PaymentMethodCreateParams } from '@stripe/stripe-js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  DetailsAddress,
  DetailsCard,
  DetailsSection,
  useDetailsForm,
} from '../../../../components/Checkout/Details';
import { COUNTRIES } from '../../../../constants/locations';
import { toSavedPhone } from '../../../../helpers/phone';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useAuth } from '../../../../providers/AuthProvider';
import { useCheckoutCtx } from '../../../../providers/CheckoutProvider';
import { useDraws } from '../../../../providers/DrawProvider';
import { AuthState } from '../../../../types/Auth';
import {
  GeneratedAddress,
  GeneratedPaymentMethod,
  useCreateAddressMutation,
  useUpdateAccountMutation,
} from '../../../../types/generated';
import { CountryCode } from '../../../../types/Locations';
import { captureError } from '../../../../utils/capture';
import { useCheckoutDetails } from '../../hooks/useCheckoutDetails';
import { useCheckoutSubscriptions } from '../../hooks/useCheckoutSubscriptions';
import { resolveStripeErrorKey } from '../../utils/stripeErrors';
import { CheckoutElementsProvider } from '../CheckoutElementsProvider';
import { DetailsAddressFields } from '../details/DetailsAddressFields';
import {
  DetailsAddressState,
  EMPTY_DETAILS_ADDRESS_STATE,
} from '../details/DetailsAddressFields.state';
import { DetailsAuthGate } from '../details/DetailsAuthGate';
import { DetailsCardFields } from '../details/DetailsCardFields';
import {
  DetailsCardState,
  EMPTY_DETAILS_CARD_STATE,
} from '../details/DetailsCardFields.state';

const toDetailsAddress = (address: GeneratedAddress): DetailsAddress => ({
  addressLine1: address.addressLine1 ?? '',
  addressLine2: address.addressLine2 ?? undefined,
  city: address.city ?? '',
  countryName: address.countryName ?? address.countryCode ?? '',
  firstName: address.firstName ?? '',
  id: address.id,
  lastName: address.lastName ?? '',
  zip: address.zip ?? '',
});

const toDetailsCard = (
  paymentMethod: GeneratedPaymentMethod,
): DetailsCard | null => {
  if (!paymentMethod.card) return null;

  return {
    brand: paymentMethod.card.brand,
    id: paymentMethod.providerPaymentMethodId,
    last4: paymentMethod.card.last4,
  };
};

const toDetailsCardFromStripe = (
  paymentMethod: PaymentMethod,
): DetailsCard | null => {
  if (!paymentMethod.card) return null;

  return {
    brand: paymentMethod.card.brand,
    id: paymentMethod.id,
    last4: paymentMethod.card.last4,
  };
};

type PersistNewCardResult =
  | { paymentMethod: PaymentMethod }
  | { error: string };

const DetailsSkeleton = () => (
  <div className="flex flex-col gap-6">
    <Skeleton className="h-12 w-full" />
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-12 w-full" />
  </div>
);

const AddDetailsContent = () => {
  const {
    artistName,
    isPreOrderFlow,
    preOrdersEnabled,
    prepareIntent,
    setDetailsValues,
    step,
  } = useCheckoutCtx();
  const { authState, emailForCode, isLoggedIn, updateUser, user } = useAuth();
  const { mainDraw } = useDraws();

  useCheckoutSubscriptions();

  const showForm = Boolean(isLoggedIn && user);
  const showPaymentCard = Boolean(mainDraw);

  const {
    addresses,
    defaultAddressId,
    defaultCardId,
    loaded,
    paymentMethods,
    refreshAddresses,
  } = useCheckoutDetails({
    enabled: showForm,
  });
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation(['checkout', 'countries']);
  const [preferredCurrency] = useCurrency();

  const [createAddressMutation] = useCreateAddressMutation();
  const [updateAccountMutation] = useUpdateAccountMutation();

  const [addressState, setAddressState] = useState<DetailsAddressState>(
    EMPTY_DETAILS_ADDRESS_STATE,
  );
  const [billingAddressState, setBillingAddressState] =
    useState<DetailsAddressState>(EMPTY_DETAILS_ADDRESS_STATE);
  const [cardState, setCardState] = useState<DetailsCardState>(
    EMPTY_DETAILS_CARD_STATE,
  );
  const [addedCards, setAddedCards] = useState<DetailsCard[]>([]);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addressErrorMessage, setAddressErrorMessage] = useState<string | null>(
    null,
  );
  const [cardErrorMessage, setCardErrorMessage] = useState<string | null>(null);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const showMarketingOptIn =
    loaded &&
    !addresses.length &&
    !user?.phoneOptedInAt &&
    !user?.phoneOptedOutAt;

  const detailsAddresses = useMemo(
    () => addresses.map(toDetailsAddress),
    [addresses],
  );
  const detailsCards = useMemo(() => {
    const saved = paymentMethods
      .map(toDetailsCard)
      .filter((card): card is DetailsCard => card !== null);
    const savedIds = new Set(saved.map((card) => card.id));

    return [...addedCards.filter((card) => !savedIds.has(card.id)), ...saved];
  }, [addedCards, paymentMethods]);

  const form = useDetailsForm({
    addresses: detailsAddresses,
    cards: detailsCards,
    defaultAddressId,
    defaultCardId: defaultCardId ?? undefined,
    isPreOrderFlow,
  });

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const persistNewAddress = useCallback(async (): Promise<string | null> => {
    const { customsCode, value } = addressState;
    if (!value?.address) return null;

    const [firstName, lastName] = (value.name ?? '').split(/[ ]{1}(.*)/);

    try {
      const response = await createAddressMutation({
        variables: {
          address: {
            isDefault: form.values.saveAddress,
            addressLine1: value.address.line1 || void 0,
            addressLine2: value.address.line2 || void 0,
            city: value.address.city || void 0,
            countryName: `${t(`countries:${value.address.country}`)}`,
            countryCode: value.address.country,
            customsCode,
            firstName,
            lastName,
            phone: value.phone ?? void 0,
            provinceName: value.address.state ?? void 0,
            zip: value.address.postal_code ?? void 0,
          },
        },
      });

      if (response.errors || !response.data?.createAddress.id) return null;

      return response.data.createAddress.id;
    } catch (error) {
      captureError(error as Error);
      return null;
    }
  }, [addressState, createAddressMutation, form.values.saveAddress, t]);

  const optInToMarketing = useCallback(
    async (phone: string) => {
      try {
        const savedPhone = toSavedPhone(phone);
        if (!savedPhone) return;

        const response = await updateAccountMutation({
          variables: {
            phone: savedPhone,
            phoneOptedInAt: new Date().toISOString(),
          },
        });

        if (response.data?.updateAccount) {
          updateUser(response.data.updateAccount);
        }
      } catch (error) {
        captureError(error as Error);
      }
    },
    [updateAccountMutation, updateUser],
  );

  const buildBillingDetails =
    useCallback((): PaymentMethodCreateParams.BillingDetails => {
      const email = user?.email ?? '';

      if (!form.values.billingSameAsShipping) {
        const billing = billingAddressState.value;
        if (!billing?.address) return { email };

        return {
          name: billing.name ?? '',
          phone: billing.phone ?? void 0,
          email,
          address: {
            line1: billing.address.line1 ?? '',
            line2: billing.address.line2 ?? '',
            city: billing.address.city ?? '',
            country: billing.address.country ?? '',
            postal_code: billing.address.postal_code ?? '',
            state: billing.address.state ?? '',
          },
        };
      }

      const savedAddress = addresses.find(
        (address) => address.id === form.values.selectedAddressId,
      );

      if (savedAddress) {
        return {
          name: [savedAddress.firstName, savedAddress.lastName].join(' '),
          phone: savedAddress.phone ?? void 0,
          email,
          address: {
            line1: savedAddress.addressLine1 ?? '',
            line2: savedAddress.addressLine2 ?? '',
            city: savedAddress.city ?? '',
            country: savedAddress.countryCode ?? '',
            postal_code: savedAddress.zip ?? '',
            state: savedAddress.provinceName ?? '',
          },
        };
      }

      const enteredAddress = addressState.value;
      if (!enteredAddress?.address) return { email };

      return {
        name: enteredAddress.name ?? '',
        phone: enteredAddress.phone ?? void 0,
        email,
        address: {
          line1: enteredAddress.address.line1 ?? '',
          line2: enteredAddress.address.line2 ?? '',
          city: enteredAddress.address.city ?? '',
          country: enteredAddress.address.country ?? '',
          postal_code: enteredAddress.address.postal_code ?? '',
          state: enteredAddress.address.state ?? '',
        },
      };
    }, [
      addresses,
      addressState.value,
      billingAddressState.value,
      form.values.billingSameAsShipping,
      form.values.selectedAddressId,
      user?.email,
    ]);

  const persistNewCard =
    useCallback(async (): Promise<PersistNewCardResult> => {
      const genericError = t('checkout:details.errors.generic');
      if (!stripe || !elements) return { error: genericError };

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) return { error: genericError };

      const response = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: buildBillingDetails(),
      });

      if (response.error) {
        return {
          error: t(`checkout:${resolveStripeErrorKey(response.error)}`),
        };
      }

      return { paymentMethod: response.paymentMethod };
    }, [buildBillingDetails, elements, stripe, t]);

  const selectCreatedAddress = useCallback(
    async (addressId: string) => {
      await refreshAddresses();
      form.setSelectedAddressId(addressId);
      setAddressState(EMPTY_DETAILS_ADDRESS_STATE);
    },
    [form, refreshAddresses],
  );

  const selectCreatedCard = useCallback(
    (card: DetailsCard) => {
      setAddedCards((previous) =>
        previous.some((existing) => existing.id === card.id)
          ? previous
          : [card, ...previous],
      );
      form.setSelectedCardId(card.id);
      setCardState(EMPTY_DETAILS_CARD_STATE);
    },
    [form],
  );

  const handleAddAddress = useCallback(async () => {
    setAddressErrorMessage(null);
    setIsSavingAddress(true);

    try {
      const addressId = await persistNewAddress();
      if (!addressId) {
        setAddressErrorMessage(t('checkout:details.errors.address'));
        return;
      }

      await selectCreatedAddress(addressId);
      form.close();
    } finally {
      setIsSavingAddress(false);
    }
  }, [form, persistNewAddress, selectCreatedAddress, t]);

  const handleAddCard = useCallback(async () => {
    setCardErrorMessage(null);
    setIsSavingCard(true);

    try {
      const result = await persistNewCard();
      if ('error' in result) {
        setCardErrorMessage(result.error);
        return;
      }

      const card = toDetailsCardFromStripe(result.paymentMethod);
      if (!card) {
        setCardErrorMessage(t('checkout:details.errors.generic'));
        return;
      }

      selectCreatedCard(card);
      form.close();
    } finally {
      setIsSavingCard(false);
    }
  }, [form, persistNewCard, selectCreatedCard, t]);

  const needsNewAddress = !form.selectedAddress;
  const needsNewCard = showPaymentCard && !form.selectedCard;
  const billingComplete =
    form.values.billingSameAsShipping || billingAddressState.complete;

  const canSubmit =
    (!needsNewAddress || addressState.complete) &&
    (!needsNewCard || (cardState.complete && billingComplete));

  const handleSubmit = useCallback(async () => {
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      let { selectedAddressId, selectedCardId } = form.values;

      if (needsNewAddress) {
        const addressId = await persistNewAddress();
        if (!addressId) {
          setErrorMessage(t('checkout:details.errors.address'));
          return;
        }

        const phone = addressState.value?.phone;
        if (showMarketingOptIn && marketingOptIn && phone) {
          void optInToMarketing(phone);
        }

        selectedAddressId = addressId;
        await selectCreatedAddress(addressId);
      }

      if (needsNewCard) {
        const result = await persistNewCard();
        if ('error' in result) {
          setErrorMessage(result.error);
          return;
        }

        const card = toDetailsCardFromStripe(result.paymentMethod);
        if (!card) {
          setErrorMessage(t('checkout:details.errors.generic'));
          return;
        }

        selectedCardId = result.paymentMethod.id;
        selectCreatedCard(card);
      }

      setDetailsValues({ ...form.values, selectedAddressId, selectedCardId });

      const shippingCountry =
        addresses.find((address) => address.id === selectedAddressId)
          ?.countryCode ??
        addressState.value?.address?.country ??
        null;
      const countryCode = COUNTRIES[shippingCountry as CountryCode]
        ? (shippingCountry as CountryCode)
        : null;
      const currencyCode = countryCode
        ? COUNTRIES[countryCode].currency
        : preferredCurrency;

      const prepared = await prepareIntent({
        countryCode,
        currencyCode,
        paymentMethodId: selectedCardId ?? null,
        preOrder: isPreOrderFlow || form.values.preOrderOptIn,
        shippingAddressId: selectedAddressId ?? null,
      });

      if (!prepared) {
        setErrorMessage(t('checkout:details.errors.generic'));
        return;
      }

      step.goNext();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    addresses,
    addressState.value,
    form.values,
    isPreOrderFlow,
    marketingOptIn,
    needsNewAddress,
    needsNewCard,
    optInToMarketing,
    persistNewAddress,
    prepareIntent,
    persistNewCard,
    preferredCurrency,
    selectCreatedAddress,
    selectCreatedCard,
    setDetailsValues,
    showMarketingOptIn,
    step,
    t,
  ]);

  const handleAddressFieldsChange = useCallback(
    (state: DetailsAddressState) => setAddressState(state),
    [],
  );
  const handleCardFieldsChange = useCallback(
    (state: DetailsCardState) => setCardState(state),
    [],
  );
  const handleBillingAddressFieldsChange = useCallback(
    (state: DetailsAddressState) => setBillingAddressState(state),
    [],
  );

  useEffect(() => {
    if (form.values.billingSameAsShipping) {
      setBillingAddressState(EMPTY_DETAILS_ADDRESS_STATE);
    }
  }, [form.values.billingSameAsShipping]);

  useEffect(() => {
    if (form.openDialog !== 'addCard') setCardErrorMessage(null);
    if (form.openDialog !== 'addAddress') setAddressErrorMessage(null);
  }, [form.openDialog]);

  const isResolvingAccount =
    !showForm &&
    ((authState === AuthState.LOADING && !emailForCode) ||
      (authState === AuthState.LOGGED_IN && !user));
  const isLoadingSavedDetails = showForm && showPaymentCard && !loaded;

  const title = isPreOrderFlow
    ? t('checkout:details.title.preOrder', { artistName })
    : t('checkout:details.title.draw', { artistName });
  const helpText = isPreOrderFlow
    ? t('checkout:details.auth.helpText.preOrder')
    : t('checkout:details.auth.helpText.draw');

  return (
    <div className="flex flex-col gap-y-9 lg:gap-y-12">
      <h2 className="text-center text-md font-bold lg:text-lg">{title}</h2>

      {!isMounted || isResolvingAccount || isLoadingSavedDetails ? (
        <DetailsSkeleton />
      ) : showForm ? (
        <DetailsSection
          addresses={detailsAddresses}
          addressFields={
            <DetailsAddressFields onChange={handleAddressFieldsChange} />
          }
          billingAddressFields={
            <CheckoutElementsProvider>
              <DetailsAddressFields
                mode="billing"
                onChange={handleBillingAddressFieldsChange}
              />
            </CheckoutElementsProvider>
          }
          addAddress={{
            canSave: addressState.complete,
            isSaving: isSavingAddress,
            errorMessage: addressErrorMessage,
            onSave: () => void handleAddAddress(),
          }}
          addCard={{
            canSave: cardState.complete && billingComplete,
            isSaving: isSavingCard,
            errorMessage: cardErrorMessage,
            onSave: () => void handleAddCard(),
          }}
          canSubmit={canSubmit}
          cardFields={<DetailsCardFields onChange={handleCardFieldsChange} />}
          cards={detailsCards}
          errorMessage={errorMessage}
          form={form}
          isSubmitting={isSubmitting}
          marketingOptIn={marketingOptIn}
          onMarketingOptInChange={setMarketingOptIn}
          onSubmit={() => void handleSubmit()}
          preOrdersEnabled={preOrdersEnabled}
          showMarketingOptIn={showMarketingOptIn}
          showPaymentCard={showPaymentCard}
        />
      ) : (
        <DetailsAuthGate helpText={helpText} />
      )}
    </div>
  );
};

export const AddDetails = () => (
  <CheckoutElementsProvider>
    <AddDetailsContent />
  </CheckoutElementsProvider>
);
